import { UppFloorPlanFurniture } from '../plan/floor-plan-element';
import {
  floorPlanInnerStrokeAttrs,
  registerFloorPlanShapePainter,
  type FloorPlanShapePaintContext,
} from './paint';

/** Main horizontal run depth (from top) and return-leg width for L-sofas in local px. */
function sofaLGeometryPx(w: number, h: number): { mainD: number; retW: number } {
  let mainD = Math.min(h * 0.45, w * 0.48);
  let retW = Math.min(w * 0.4, h * 0.52);
  mainD = Math.max(2, Math.min(mainD, h - 2));
  retW = Math.max(2, Math.min(retW, w - 2));
  if (mainD >= h - 1) {
    mainD = Math.max(2, h * 0.35);
  }
  if (retW >= w - 1) {
    retW = Math.max(2, w * 0.35);
  }
  return { mainD, retW };
}

/** English comment normalized. */
function sofaCushionSideMarginPx(w: number): number {
  return Math.max(1, Math.min(w * 0.1, w * 0.18 - 0.5));
}

/** English comment normalized. */
const SOFA_L_CUSHION_ARM_THICKNESS_FRACTION = 0.5;

/** English comment normalized. */
function sofaLCushionRightBottomInsetPx(w: number, h: number): number {
  return Math.max(1, Math.min(w, h) * 0.04);
}

/**
 * English comment normalized.
 * from above (`mainD − mdi`) and to the left (`retW − rwi`); the outer frame does not change.
 */
function sofaLCushionAsymmetricInsetPx(
  w: number,
  h: number,
  mainD: number,
  retW: number,
  mdi: number,
  rwi: number,
): { mRight: number; mBottom: number } {
  const floor = sofaLCushionRightBottomInsetPx(w, h);
  const bandTop = Math.max(0, mainD - mdi);
  const bandLeft = Math.max(0, retW - rwi);
  const capR = Math.max(0, w - rwi - 3);
  const capB = Math.max(0, h - mdi - 3);
  const mRight = Math.min(Math.max(1, floor, bandTop), capR);
  const mBottom = Math.min(Math.max(1, floor, bandLeft), capB);
  return { mRight, mBottom };
}

/** Shortens the arms of the L outline (to the right and down) without moving the top-left anchor. */
function sofaLArmDrawExtentPx(w: number, h: number, mainD: number, retW: number): { wD: number; hD: number } {
  const s0 = Math.max(2, Math.min(w, h) * 0.1);
  const s = Math.min(s0, Math.max(0, w - retW - 4), Math.max(0, h - mainD - 4));
  return { wD: Math.max(retW + 3, w - s), hD: Math.max(mainD + 3, h - s) };
}

/** English comment normalized. */
function sofaCushionDepthPx(h: number, maxDepth: number): number {
  const target = Math.max(h * 0.5, h * 0.4);
  return Math.max(2, Math.min(target, maxDepth, h - 1));
}

/** Rounded corners on sofa body (linear + L top two corners), matches visual family. */
function sofaOuterCornerRadiusPx(w: number, h: number): number {
  return Math.max(0, Math.min(w, h) * 0.12);
}

/** English comment normalized. */
function sofaLBodyCornerRadiusPx(w: number, h: number, mainD: number, retW: number): number {
  const xLeg = w - retW;
  return Math.max(
    0,
    Math.min(
      sofaOuterCornerRadiusPx(w, h),
      w / 2 - 0.5,
      h / 2 - 0.5,
      mainD - 1,
      retW - 1,
      xLeg - 1,
      (w - retW) / 2 - 0.35,
      retW / 2 - 0.35,
      xLeg / 2 - 0.35,
      mainD / 2 - 0.35,
      (h - mainD) / 2 - 0.35,
    ),
  );
}

const SOFA_CUSHION_FILL = { opacity: 0.35, listening: false };

/** English comment normalized. */
function sofaLinearCushionPath(context: any, w: number, h: number): void {
  const mx = sofaCushionSideMarginPx(w);
  const ch = sofaCushionDepthPx(h, h);
  const x0 = mx;
  const x1 = w - mx;
  if (x1 <= x0 + 2 || ch < 2) {
    return;
  }
  const cr = Math.max(
    0,
    Math.min((x1 - x0) * 0.14, ch * 0.22, mx * 0.95, (x1 - x0) / 2 - 0.25, ch - 0.5),
  );
  context.beginPath();
  if (cr <= 0) {
    context.rect(x0, 0, x1 - x0, ch);
    return;
  }
  context.moveTo(x0, 0);
  context.lineTo(x1, 0);
  context.lineTo(x1, ch - cr);
  context.arc(x1 - cr, ch - cr, cr, 0, Math.PI / 2, false);
  context.lineTo(x0 + cr, ch);
  context.arc(x0 + cr, ch - cr, cr, Math.PI / 2, Math.PI, false);
  context.lineTo(x0, 0);
  context.closePath();
}

/**
 * L exterior TOLEFT. Arms shortened (`sofaLArmDrawExtentPx`). Inner elbow rounded; **top-left corner
 * English comment normalized.
 * English comment normalized.
 *
 * English comment normalized.
 */
function sofaLToLeftOuterPath(
  context: any,
  w: number,
  h: number,
  mainD: number,
  retW: number,
  radiiRef?: { w: number; h: number; mainD?: number; retW?: number },
  squareOuterArmCaps?: boolean,
): void {
  const { wD, hD } = sofaLArmDrawExtentPx(w, h, mainD, retW);
  const rPath = radiiRef != null
    ? Math.min(
        sofaLBodyCornerRadiusPx(
          radiiRef.w,
          radiiRef.h,
          radiiRef.mainD ?? mainD,
          radiiRef.retW ?? retW,
        ),
        sofaLBodyCornerRadiusPx(wD, hD, mainD, retW),
      )
    : sofaLBodyCornerRadiusPx(wD, hD, mainD, retW);

  context.beginPath();
  if (rPath <= 0) {
    context.moveTo(0, 0);
    context.lineTo(wD, 0);
    context.lineTo(wD, mainD);
    context.lineTo(retW, mainD);
    context.lineTo(retW, hD);
    context.lineTo(0, hD);
    context.closePath();
    return;
  }
  const r = rPath;
  context.moveTo(0, 0);
  if (squareOuterArmCaps) {
    context.lineTo(wD, 0);
    context.lineTo(wD, mainD - r);
  } else {
    context.lineTo(wD - r, 0);
    context.arc(wD - r, r, r, -Math.PI / 2, 0, false);
    context.lineTo(wD, mainD - r);
  }
  context.arc(wD - r, mainD - r, r, 0, Math.PI / 2, false);
  context.lineTo(retW + r, mainD);
  context.arc(retW + r, mainD + r, r, -Math.PI / 2, Math.PI, true);
  context.lineTo(retW, hD - r);
  context.arc(retW - r, hD - r, r, 0, Math.PI / 2, false);
  if (squareOuterArmCaps) {
    context.lineTo(r, hD);
    context.lineTo(0, hD);
    context.lineTo(0, r);
  } else {
    context.lineTo(r, hD);
    context.arc(r, hD - r, r, Math.PI / 2, Math.PI, false);
    context.lineTo(0, r);
  }
  context.lineTo(0, 0);
  context.closePath();
}

/**
 * English comment normalized.
 * right/bottom (`wi = w − mRight`, `hi = h − mBottom`) aligned to the thickness of upper/left band.
 */
function sofaLToLeftCushionPath(context: any, w: number, h: number, mainD: number, retW: number): void {
  const f = SOFA_L_CUSHION_ARM_THICKNESS_FRACTION;
  const mdi = Math.max(2, mainD * f);
  const rwi = Math.max(2, retW * f);
  if (mdi >= mainD || rwi >= retW) {
    return;
  }
  const { mRight, mBottom } = sofaLCushionAsymmetricInsetPx(w, h, mainD, retW, mdi, rwi);
  if (mRight < 1 || mBottom < 1) {
    return;
  }
  const wi = w - mRight;
  const hi = h - mBottom;
  if (wi < 6 || hi < 6 || wi <= rwi + 2 || hi <= mdi + 2) {
    return;
  }
  sofaLToLeftOuterPath(context, wi, hi, mdi, rwi, { w, h, mainD, retW }, true);
}

/**
 * English comment normalized.
 * English comment normalized.
 */
function sofaLToRightOuterPath(
  context: any,
  w: number,
  h: number,
  mainD: number,
  retW: number,
  radiiRef?: { w: number; h: number; mainD?: number; retW?: number },
  squareOuterArmCaps?: boolean,
): void {
  const { wD, hD } = sofaLArmDrawExtentPx(w, h, mainD, retW);
  const xLeg = wD - retW;
  const rPath = radiiRef != null
    ? Math.min(
        sofaLBodyCornerRadiusPx(
          radiiRef.w,
          radiiRef.h,
          radiiRef.mainD ?? mainD,
          radiiRef.retW ?? retW,
        ),
        sofaLBodyCornerRadiusPx(wD, hD, mainD, retW),
      )
    : sofaLBodyCornerRadiusPx(wD, hD, mainD, retW);

  context.beginPath();
  if (rPath <= 0) {
    context.moveTo(0, 0);
    context.lineTo(wD, 0);
    context.lineTo(wD, hD);
    context.lineTo(xLeg, hD);
    context.lineTo(xLeg, mainD);
    context.lineTo(0, mainD);
    context.closePath();
    return;
  }
  const r = rPath;
  context.moveTo(r, 0);
  if (squareOuterArmCaps) {
    context.lineTo(wD, 0);
    context.lineTo(wD, hD - r);
  } else {
    context.lineTo(wD, 0);
    context.lineTo(wD, hD - r);
  }
  context.arc(wD - r, hD - r, r, 0, Math.PI / 2, false);
  context.lineTo(xLeg + r, hD);
  context.arc(xLeg + r, hD - r, r, Math.PI / 2, Math.PI, false);
  context.lineTo(xLeg, mainD + r);
  context.arc(xLeg - r, mainD + r, r, 0, (3 * Math.PI) / 2, true);
  context.lineTo(r, mainD);
  if (squareOuterArmCaps) {
    context.lineTo(0, mainD);
    context.lineTo(0, r);
  } else {
    context.arc(r, mainD - r, r, Math.PI / 2, Math.PI, false);
    context.lineTo(0, r);
  }
  context.arc(r, r, r, Math.PI, (3 * Math.PI) / 2, false);
  context.closePath();
}

/**
 * English comment normalized.
 * English comment normalized.
 * English comment normalized.
 * English comment normalized.
 */
function sofaLToRightCushionPath(context: any, w: number, h: number, mainD: number, retW: number): void {
  const f = SOFA_L_CUSHION_ARM_THICKNESS_FRACTION;
  const mdi = Math.max(2, mainD * f);
  const rwi = Math.max(2, retW * f);
  if (mdi >= mainD || rwi >= retW) {
    return;
  }
  const { mRight, mBottom } = sofaLCushionAsymmetricInsetPx(w, h, mainD, retW, mdi, rwi);
  if (mRight < 1 || mBottom < 1) {
    return;
  }
  const wi = w - mRight;
  const hi = h - mBottom;
  if (wi < 6 || hi < 6 || wi <= rwi + 2 || hi <= mdi + 2) {
    return;
  }
  const { wD: wDOut } = sofaLArmDrawExtentPx(w, h, mainD, retW);
  const { wD: wDIn } = sofaLArmDrawExtentPx(wi, hi, mdi, rwi);
  const dx = wDOut - wDIn;
  context.save();
  context.translate(dx, 0);
  sofaLToRightOuterPath(context, wi, hi, mdi, rwi, { w, h, mainD, retW }, true);
  context.restore();
}

function paintFurnitureSofaLinear(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h, theme, innerStrokeUsesSelection } = ctx;
  const { stroke, strokeWidth } = floorPlanInnerStrokeAttrs(theme, innerStrokeUsesSelection);
  group.add(
    new K.Rect({
      x: 0,
      y: 0,
      width: w,
      height: h,
      cornerRadius: Math.min(w, h) * 0.12,
      fill: theme.elementFill,
      stroke,
      strokeWidth,
      listening: false,
    }),
  );
  group.add(
    new K.Shape({
      fill: theme.roomStroke,
      ...SOFA_CUSHION_FILL,
      sceneFunc(context: any, shape: any) {
        const mx = sofaCushionSideMarginPx(w);
        const ch = sofaCushionDepthPx(h, h);
        if (w - 2 * mx <= 2 || ch < 2) {
          return;
        }
        sofaLinearCushionPath(context, w, h);
        context.fillShape(shape);
      },
    }),
  );
}

/** L-shaped sofa TOLEFT variant with cushion inset and arm contour. */
function paintFurnitureSofaLToLeft(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h, theme, innerStrokeUsesSelection } = ctx;
  const { stroke, strokeWidth } = floorPlanInnerStrokeAttrs(theme, innerStrokeUsesSelection);
  const { mainD, retW } = sofaLGeometryPx(w, h);

  group.add(
    new K.Shape({
      fill: theme.elementFill,
      stroke,
      strokeWidth,
      listening: false,
      sceneFunc(context: any, shape: any) {
        sofaLToLeftOuterPath(context, w, h, mainD, retW);
        context.fillStrokeShape(shape);
      },
    }),
  );
  group.add(
    new K.Shape({
      fill: theme.roomStroke,
      ...SOFA_CUSHION_FILL,
      listening: false,
      sceneFunc(context: any, shape: any) {
        sofaLToLeftCushionPath(context, w, h, mainD, retW);
        context.fillShape(shape);
      },
    }),
  );
}

/** L-shaped sofa TORIGHT variant with cushion inset and arm contour. */
function paintFurnitureSofaLToRight(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h, theme, innerStrokeUsesSelection } = ctx;
  const { stroke, strokeWidth } = floorPlanInnerStrokeAttrs(theme, innerStrokeUsesSelection);
  const { mainD, retW } = sofaLGeometryPx(w, h);

  group.add(
    new K.Shape({
      fill: theme.elementFill,
      stroke,
      strokeWidth,
      listening: false,
      sceneFunc(context: any, shape: any) {
        sofaLToRightOuterPath(context, w, h, mainD, retW);
        context.fillStrokeShape(shape);
      },
    }),
  );
  group.add(
    new K.Shape({
      fill: theme.roomStroke,
      ...SOFA_CUSHION_FILL,
      listening: false,
      sceneFunc(context: any, shape: any) {
        sofaLToRightCushionPath(context, w, h, mainD, retW);
        context.fillShape(shape);
      },
    }),
  );
}

function paintFurnitureSofa(ctx: FloorPlanShapePaintContext): void {
  const el = ctx.element;
  if (el instanceof UppFloorPlanFurniture && el.furnitureClass === 'sofa') {
    if (el.sofaShape === 'TOLEFT') {
      paintFurnitureSofaLToLeft(ctx);
      return;
    }
    if (el.sofaShape === 'TORIGHT') {
      paintFurnitureSofaLToRight(ctx);
      return;
    }
  }
  paintFurnitureSofaLinear(ctx);
}

const BILLIARD_BROWN = '#8b4513';
const BILLIARD_GREEN = '#008000';

/** v3 `billiards.js` — wood border, green felt, pockets, triangle rack, two cues. */
function paintFurnitureBilliard(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h } = ctx;
  const bw = Math.max(2, w / 30);

  group.add(
    new K.Rect({
      x: 0,
      y: 0,
      width: w,
      height: h,
      fill: BILLIARD_BROWN,
      cornerRadius: bw * 0.4,
      listening: false,
    }),
  );
  group.add(
    new K.Rect({
      x: bw,
      y: bw,
      width: w - 2 * bw,
      height: h - 2 * bw,
      fill: BILLIARD_GREEN,
      listening: false,
    }),
  );

  const ps = bw * 1.3;
  const pocketCenters: [number, number][] = [
    [bw, bw],
    [w - bw, bw],
    [bw, h - bw],
    [w - bw, h - bw],
    [w / 2, bw],
    [w / 2, h - bw],
  ];
  for (const [px, py] of pocketCenters) {
    group.add(
      new K.Circle({
        x: px,
        y: py,
        radius: ps / 2,
        fill: '#111',
        listening: false,
      }),
    );
  }

  const bd = bw * 1.2;
  const startX = w / 8 + bd * 5;
  const startY = h / 2;
  for (let row = 0; row < 5; row++) {
    for (let j = 0; j <= row; j++) {
      const xCoord = startX - bd * row;
      const yCoord = startY + bd * j - (bd * row) / 2;
      group.add(
        new K.Circle({
          x: xCoord,
          y: yCoord,
          radius: bd / 2,
          fill: '#e8e8e8',
          stroke: '#222',
          strokeWidth: 0.5,
          listening: false,
        }),
      );
    }
  }

  const sl = w / 2;
  const bwCue = 4;
  const tw = 2;
  const tl = sl / 10;
  const cueY1 = h / 4;
  const cueY2 = (3 * h) / 4 - bwCue;

  const drawCue = (sx: number, sy: number) => {
    group.add(
      new K.Line({
        points: [
          sx,
          sy,
          sx,
          sy + bwCue,
          sx + sl - tl,
          sy + bwCue,
          sx + sl,
          sy + bwCue / 2 + tw / 2,
          sx + sl,
          sy + bwCue / 2 - tw / 2,
          sx + sl - tl,
          sy,
        ],
        closed: true,
        fill: BILLIARD_BROWN,
        stroke: '#4a3728',
        strokeWidth: 0.5,
        listening: false,
      }),
    );
  };
  drawCue(w / 3, cueY1);
  drawCue(w / 3, cueY2);
}

const FOOTBALL_BROWN = '#8b4513';
const FOOTBALL_GREEN = '#008000';

/**
 * v3 `football.js` — wood frame, green pitch, markings, goals, rods and players.
 * Simplified goal mirroring vs p5 `rotate(PI)` but visually aligned.
 */
function paintFurnitureFootball(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h } = ctx;
  const bw = h / 5;
  const bh = w / 40;
  const ix = bw;
  const iy = bh;
  // Wide aspect ratio can make h - 2*bh negative (bh = w/40); inner pitch must stay non-negative for arcs/geometry.
  const iw = Math.max(0, w - 2 * bw);
  const ih = Math.max(0, h - 2 * bh);
  const lineW = Math.max(0.8, w / 100);

  group.add(
    new K.Rect({
      x: 0,
      y: 0,
      width: w,
      height: h,
      fill: FOOTBALL_BROWN,
      cornerRadius: bw / 2,
      listening: false,
    }),
  );
  group.add(
    new K.Rect({
      x: ix,
      y: iy,
      width: iw,
      height: ih,
      fill: FOOTBALL_GREEN,
      listening: false,
    }),
  );

  group.add(
    new K.Rect({
      x: ix,
      y: iy,
      width: iw,
      height: ih,
      stroke: '#fff',
      strokeWidth: lineW,
      listening: false,
    }),
  );
  group.add(
    new K.Line({
      points: [ix + iw / 2, iy, ix + iw / 2, iy + ih],
      stroke: '#fff',
      strokeWidth: lineW,
      listening: false,
    }),
  );
  group.add(
    new K.Ellipse({
      x: ix + iw / 2,
      y: iy + ih / 2,
      radiusX: iw / 8,
      radiusY: iw / 8,
      stroke: '#fff',
      strokeWidth: lineW,
      listening: false,
    }),
  );

  const gh = ih / 2;
  const aw = iw / 6;
  const sh = gh / 2;
  const sw = aw / 2;
  const cy = iy + ih / 2;
  const rArc = sh * 0.4;

  const goalLeft = new K.Group({ listening: false });
  group.add(goalLeft);
  goalLeft.add(
    new K.Rect({
      x: ix,
      y: iy + (ih - gh) / 2,
      width: aw,
      height: gh,
      stroke: '#fff',
      strokeWidth: lineW,
      listening: false,
    }),
  );
  goalLeft.add(
    new K.Rect({
      x: ix,
      y: iy + (ih - sh) / 2,
      width: sw,
      height: sh,
      stroke: '#fff',
      strokeWidth: lineW,
      listening: false,
    }),
  );
  goalLeft.add(
    new K.Shape({
      stroke: '#fff',
      strokeWidth: lineW,
      listening: false,
      sceneFunc(context: any, shape: any) {
        context.beginPath();
        context.arc(ix + aw, cy, rArc, -Math.PI / 2, Math.PI / 2, false);
        context.strokeShape(shape);
      },
    }),
  );

  const gxR = ix + iw - aw;
  group.add(
    new K.Rect({
      x: gxR,
      y: iy + (ih - gh) / 2,
      width: aw,
      height: gh,
      stroke: '#fff',
      strokeWidth: lineW,
      listening: false,
    }),
  );
  group.add(
    new K.Rect({
      x: gxR + aw - sw,
      y: iy + (ih - sh) / 2,
      width: sw,
      height: sh,
      stroke: '#fff',
      strokeWidth: lineW,
      listening: false,
    }),
  );
  group.add(
    new K.Shape({
      stroke: '#fff',
      strokeWidth: lineW,
      listening: false,
      sceneFunc(context: any, shape: any) {
        context.beginPath();
        context.arc(gxR, cy, rArc, -Math.PI / 2, Math.PI / 2, true);
        context.strokeShape(shape);
      },
    }),
  );

  const br = iw / 9;
  const rp = [1, 2, 3, 4, 4, 3, 2, 1];
  const pr = iw / 20;
  const re = iw / 8;
  const hl = iw / 10;
  const rt = Math.max(0.5, iw / 60);
  const ht = rt + 2;
  const play = new K.Group({ x: 0, y: 0, listening: false });
  group.add(play);

  for (let i = 0; i < 8; i++) {
    const posX = ix + (i + 1) * br;
    play.add(
      new K.Rect({
        x: posX - rt / 2,
        y: iy - re,
        width: rt,
        height: ih + 2 * re,
        fill: '#222',
        stroke: '#ccc',
        strokeWidth: 0.5,
        listening: false,
      }),
    );
    if ([0, 1, 3, 5].includes(i)) {
      play.add(
        new K.Rect({
          x: posX - ht / 2,
          y: iy - re,
          width: ht,
          height: hl,
          fill: FOOTBALL_BROWN,
          listening: false,
        }),
      );
    }
    if ([2, 4, 6, 7].includes(i)) {
      play.add(
        new K.Rect({
          x: posX - ht / 2,
          y: iy + ih + re - hl,
          width: ht,
          height: hl,
          fill: FOOTBALL_BROWN,
          listening: false,
        }),
      );
    }
    const players = rp[i] ?? 1;
    const between = ih / (players + 1);
    for (let j = 0; j < players; j++) {
      const posY = iy + (j + 1) * between;
      play.add(
        new K.Circle({
          x: posX,
          y: posY,
          radius: pr / 2,
          fill: i % 2 === 0 ? '#f00' : '#00f',
          stroke: '#111',
          strokeWidth: 0.5,
          listening: false,
        }),
      );
    }
  }
}

function paintFurnitureGenericRect(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h, theme, innerStrokeUsesSelection } = ctx;
  const { stroke, strokeWidth } = floorPlanInnerStrokeAttrs(theme, innerStrokeUsesSelection);
  group.add(
    new K.Rect({
      x: 0,
      y: 0,
      width: w,
      height: h,
      fill: theme.elementFill,
      stroke,
      strokeWidth,
      dash: [6, 4],
      listening: false,
    }),
  );
}

function paintFurnitureGenericRound(ctx: FloorPlanShapePaintContext): void {
  const { K, group, widthPx: w, heightPx: h, theme, innerStrokeUsesSelection } = ctx;
  const { stroke, strokeWidth } = floorPlanInnerStrokeAttrs(theme, innerStrokeUsesSelection);
  group.add(
    new K.Ellipse({
      x: w / 2,
      y: h / 2,
      radiusX: w / 2,
      radiusY: h / 2,
      fill: theme.elementFill,
      stroke,
      strokeWidth,
      listening: false,
    }),
  );
}

registerFloorPlanShapePainter('furniture:sofa', paintFurnitureSofa);
registerFloorPlanShapePainter('furniture:billiard', paintFurnitureBilliard);
registerFloorPlanShapePainter('furniture:football', paintFurnitureFootball);
registerFloorPlanShapePainter('furniture:generic:GENRCT', paintFurnitureGenericRect);
registerFloorPlanShapePainter('furniture:generic:GENRND', paintFurnitureGenericRound);
